import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Send, RefreshCw, Trash2, Edit2, Zap, StopCircle, Clock, FileText, BookOpen, Eye, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MarkdownEditor from "@/components/MarkdownEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function OutreachTab({ pw }) {
  const H = useMemo(() => ({ "X-Admin-Password": pw }), [pw]);
  const [sub, setSub] = useState("prospects");

  return (
    <div className="space-y-6" data-testid="admin-outreach">
      <div>
        <h2 className="text-2xl font-bold text-[#0D2F4D] flex items-center gap-2">
          <Zap className="h-6 w-6" />Cold Outreach
        </h2>
        <p className="text-sm text-[#6F8FA8] mt-1 max-w-2xl">
          Import Apollo leads, enroll them into your 3-step sequence, preview + edit the first email personally,
          and let the engine send the follow-ups automatically. Sent from
          {" "}<code className="text-[#0D2F4D]">no-reply@theditchcareers.com.au</code>.
        </p>
      </div>

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-[#F2F2EF] p-1.5 rounded-2xl h-auto">
          {["prospects","sequence","templates","enrollments"].map(k => (
            <TabsTrigger key={k} value={k} data-testid={`outreach-tab-${k}`}
              className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl capitalize">
              {k}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="prospects" className="mt-6"><Prospects H={H}/></TabsContent>
        <TabsContent value="sequence" className="mt-6"><SequenceEditor H={H}/></TabsContent>
        <TabsContent value="templates" className="mt-6"><CustomTemplates H={H}/></TabsContent>
        <TabsContent value="enrollments" className="mt-6"><Enrollments H={H}/></TabsContent>
      </Tabs>
    </div>
  );
}

/* ── Prospects ────────────────────────────────────────────────────── */
function Prospects({ H }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [file, setFile] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [enrolling, setEnrolling] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/outreach/prospects`, { headers: H })
      .then(r => { setRows(r.data.prospects || []); setTotal(r.data.total || 0); })
      .catch(e => toast.error(e?.response?.data?.detail || e.message));
    axios.get(`${API}/admin/outreach/sequences`, { headers: H })
      .then(r => setSequences(r.data.sequences || []))
      .catch(() => {});
  }, [H]);
  useEffect(() => { load(); }, [load]);

  const importCsv = async () => {
    if (!file) return toast.error("Choose a CSV first");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/outreach/import-apollo`, fd, {
        headers: { ...H, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${r.data.inserted} · updated ${r.data.updated} · skipped ${r.data.skipped}`);
      setFile(null);
      load();
    } catch (e) { toast.error(`Import failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const startEnroll = async (prospect) => {
    if (!sequences.length) return toast.error("No sequence defined — go to Sequence tab first");
    try {
      const r = await axios.post(`${API}/admin/outreach/enroll`,
        { prospect_id: prospect.id, sequence_id: sequences[0].id }, { headers: H });
      setEnrolling(r.data);
    } catch (e) { toast.error(`Enroll failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this prospect?")) return;
    try {
      await axios.delete(`${API}/admin/outreach/prospects/${id}`, { headers: H });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const moveToActive = async (p) => {
    // Promote a cold prospect straight into Active Employers.
    // Reuses the standard workspace employer create endpoint — it's
    // idempotent on (company_domain, contact_email) so re-clicks
    // upsert instead of duplicating.
    if (!p.email) { toast.error("Prospect has no email — can't create an employer."); return; }
    if (!window.confirm(`Move ${p.company || p.email} to Active Employers?`)) return;
    try {
      const body = {
        company_name: p.company || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email,
        contact_email: p.email,
        contact_first_name: p.first_name || "",
        contact_last_name: p.last_name || "",
        company_domain: (p.website || "").replace(/^https?:\/\//i, "").replace(/\/.*$/, ""),
        industry: p.industry || "",
        location: p.location || "",
        recruitment_status: "active_client",
        source: "outreach_prospect",
        tags: ["from-outreach"],
        notes: p.title ? `Title on Apollo: ${p.title}` : "",
      };
      await axios.post(`${API}/admin/workspace/employers`, body, { headers: H });
      // Also remove the prospect so the same person doesn't sit on both
      // lists — matches Cara's mental model of "moved" (not copied).
      await axios.delete(`${API}/admin/outreach/prospects/${p.id}`, { headers: H });
      toast.success(`${body.company_name} is now an Active Employer.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Move failed.");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 flex flex-wrap items-center gap-3" data-testid="outreach-import-bar">
        <div className="flex-1 min-w-[260px]">
          <p className="text-sm font-bold text-[#0D2F4D] mb-1">Import Apollo leads (CSV)</p>
          <p className="text-xs text-[#6F8FA8]">Export from Apollo → any columns you like. Matches email, first/last name, company, title, website, LinkedIn, industry, location automatically.</p>
        </div>
        <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0])} data-testid="outreach-csv-file"
          className="text-xs file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-bold file:bg-[#F2F2EF] file:text-[#0D2F4D] file:cursor-pointer" />
        <Button className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90"
          onClick={importCsv} disabled={!file} data-testid="outreach-csv-import">
          <Upload className="h-4 w-4 mr-1"/>Import
        </Button>
      </Card>

      <div className="flex items-center gap-3 text-sm text-[#0D2F4D]">
        <b>{total}</b> prospects
        <Button variant="outline" size="sm" onClick={load} data-testid="outreach-prospects-refresh"><RefreshCw className="h-4 w-4"/></Button>
      </div>

      <Card>
        <Table data-testid="outreach-prospects-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="hidden md:table-cell">Email</TableHead>
              <TableHead className="hidden lg:table-cell">Website</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(p => (
              <TableRow key={p.id} data-testid={`outreach-prospect-${p.id}`}>
                <TableCell className="font-semibold text-[#0D2F4D]">
                  {[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}
                </TableCell>
                <TableCell className="text-sm">{p.company || "—"}</TableCell>
                <TableCell className="text-xs text-[#6F8FA8]">{p.title || "—"}</TableCell>
                <TableCell className="text-xs hidden md:table-cell">{p.email}</TableCell>
                <TableCell className="text-xs hidden lg:table-cell">{p.website || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" className="bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F]/90"
                    onClick={() => startEnroll(p)}
                    data-testid={`outreach-enroll-${p.id}`}>
                    <Send className="h-3.5 w-3.5 mr-1"/>Enroll
                  </Button>
                  <Button size="sm" variant="outline" className="border-[#0D2F4D] text-[#0D2F4D] hover:bg-[#0D2F4D] hover:text-[#C6D21F]"
                    onClick={() => moveToActive(p)}
                    title="Promote this prospect straight into Active Employers"
                    data-testid={`outreach-move-active-${p.id}`}>
                    <ArrowRight className="h-3.5 w-3.5 mr-1"/>Move to Active
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`outreach-delete-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500"/>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (<TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-[#6F8FA8]">No prospects yet — import an Apollo CSV to start.</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </Card>

      {enrolling && <PreviewComposer data={enrolling} H={H} onClose={() => setEnrolling(null)} onSent={() => { setEnrolling(null); load(); }}/>}
    </div>
  );
}

function PreviewComposer({ data, H, onClose, onSent }) {
  const [subject, setSubject] = useState(data.subject);
  const [body, setBody] = useState(data.body);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/outreach/custom-templates`, { headers: H })
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => {});
  }, [H]);

  const loadTemplate = (tid) => {
    const t = templates.find(x => x.id === tid);
    if (!t) return;
    if (window.confirm(`Replace current draft with template "${t.name}"?`)) {
      setSubject(t.subject);
      setBody(t.body);
    }
  };

  const send = async () => {
    if (!window.confirm(`Send email 1 of the sequence to ${data.prospect.first_name} at ${data.prospect.email}?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/outreach/send-step1`,
        { enrollment_id: data.enrollment_id, subject, body }, { headers: H });
      toast.success(`Sent. Follow-up scheduled for ${r.data.next_send_at?.slice(0,10) || "—"}`);
      onSent();
    } catch (e) { toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`); }
    finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="outreach-composer">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-4">
            <span>Preview + edit — {data.step_label}</span>
            {templates.length > 0 && (
              <Select onValueChange={loadTemplate}>
                <SelectTrigger className="w-[220px] text-xs" data-testid="composer-load-template">
                  <SelectValue placeholder="Load Custom Template…"/>
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id} data-testid={`composer-tpl-${t.id}`}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-[#F5F3EE] rounded-lg p-3 text-xs">
            <b>To:</b> {data.prospect.first_name} {data.prospect.last_name} · {data.prospect.email}<br/>
            <b>Company:</b> {data.prospect.company} · {data.prospect.title}
          </div>
          <div>
            <label className="text-xs font-bold text-[#6F8FA8] uppercase tracking-wide">Subject</label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="outreach-subject"/>
          </div>
          <div>
            <label className="text-xs font-bold text-[#6F8FA8] uppercase tracking-wide">Body — auto-wrapped in the Ditch brand shell on send</label>
            <MarkdownEditor value={body} onChange={setBody} rows={14} testId="outreach-body-editor"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="outreach-composer-cancel">Cancel</Button>
          <Button
            variant="outline"
            onClick={() => setPreviewing(true)}
            disabled={!subject || !body}
            data-testid="outreach-composer-preview"
          >
            <Eye className="h-4 w-4 mr-1"/>Preview
          </Button>
          <Button className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90" onClick={send} disabled={sending} data-testid="outreach-composer-send">
            <Send className="h-4 w-4 mr-1"/>{sending ? "Sending…" : "Send email 1"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <BrandedPreviewDialog
        open={previewing}
        onClose={() => setPreviewing(false)}
        subject={subject}
        body={body}
        H={H}
        brand="careers"
        label={`To ${data.prospect.first_name || data.prospect.email}`}
      />
    </Dialog>
  );
}

/* ── Branded shell preview ────────────────────────────────────────── */
function BrandedPreviewDialog({ open, onClose, subject, body, H, brand = "careers", label = "Preview" }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true); setErr(""); setHtml("");
    axios.post(`${API}/admin/outreach/preview`, { subject, body, brand }, { headers: H })
      .then(r => setHtml(r.data.html || ""))
      .catch(e => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [open, subject, body, brand, H]);

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl h-[92vh] flex flex-col p-0" data-testid="branded-preview-dialog">
        <DialogHeader className="px-6 py-4 border-b border-[#E4E7EA] shrink-0">
          <DialogTitle className="flex items-center gap-2 text-[#0D2F4D]">
            <Eye className="h-4 w-4"/> {label} — branded shell preview
            <Badge className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D] ml-2 capitalize">{brand}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-[#F2F2EF] px-4 py-4">
          {loading && <p className="text-sm text-[#6F8FA8] p-6">Rendering…</p>}
          {err && <p className="text-sm text-red-600 p-6">Preview failed: {err}</p>}
          {!loading && !err && (
            <iframe
              title="branded-preview"
              srcDoc={html}
              data-testid="branded-preview-iframe"
              className="w-full h-full bg-white rounded-lg shadow-sm border border-[#E4E7EA]"
              sandbox=""
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Custom Templates library ─────────────────────────────────────── */
function CustomTemplates({ H }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/outreach/custom-templates`, { headers: H })
      .then(r => setRows(r.data.templates || []))
      .catch(e => toast.error(e?.response?.data?.detail || e.message));
  }, [H]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ id: null, name: "", subject: "", body: "", description: "", tags: [] });

  const save = async () => {
    if (!editing.name || !editing.subject || !editing.body) return toast.error("Name, subject, body required");
    try {
      await axios.post(`${API}/admin/outreach/custom-templates`, editing, { headers: H });
      toast.success("Template saved");
      setEditing(null); load();
    } catch (e) { toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/admin/outreach/custom-templates/${id}`, { headers: H });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4" data-testid="outreach-custom-templates">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#0D2F4D] flex items-center gap-2"><BookOpen className="h-5 w-5"/>Custom Templates</h3>
          <p className="text-xs text-[#6F8FA8]">Save polished emails once, reuse them from the Sequence editor or the Preview + Edit composer.</p>
        </div>
        <Button className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90" onClick={startNew} data-testid="custom-tpl-new">
          <FileText className="h-4 w-4 mr-1"/>New template
        </Button>
      </div>

      <Card>
        <Table data-testid="custom-tpl-table">
          <TableHeader><TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(t => (
              <TableRow key={t.id} data-testid={`custom-tpl-row-${t.id}`}>
                <TableCell className="font-semibold text-[#0D2F4D]">{t.name}</TableCell>
                <TableCell className="text-sm max-w-[300px] truncate">{t.subject}</TableCell>
                <TableCell className="text-xs text-[#6F8FA8]">{t.updated_at?.slice(0,10)}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`custom-tpl-edit-${t.id}`}><Edit2 className="h-3.5 w-3.5"/></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t.id)} data-testid={`custom-tpl-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (<TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-[#6F8FA8]">No custom templates yet. Save your polished cold emails here.</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </Card>

      {editing && (
        <Dialog open onOpenChange={o => !o && setEditing(null)}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="custom-tpl-editor">
            <DialogHeader><DialogTitle>{editing.id ? "Edit template" : "New custom template"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-[#6F8FA8] uppercase tracking-wide">Template name</label>
                <Input value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} data-testid="custom-tpl-name"/>
              </div>
              <div>
                <label className="text-xs font-bold text-[#6F8FA8] uppercase tracking-wide">Subject</label>
                <Input value={editing.subject} onChange={e => setEditing({...editing, subject: e.target.value})} data-testid="custom-tpl-subject"/>
              </div>
              <div>
                <label className="text-xs font-bold text-[#6F8FA8] uppercase tracking-wide">Body — bold, italics, lists, {`{{tokens}}`} — auto-wrapped in brand shell on send</label>
                <MarkdownEditor value={editing.body} onChange={(v) => setEditing({...editing, body: v})} rows={16} testId="custom-tpl-body"/>
              </div>
              <div>
                <label className="text-xs font-bold text-[#6F8FA8] uppercase tracking-wide">Description (admin note)</label>
                <Input value={editing.description || ""} onChange={e => setEditing({...editing, description: e.target.value})} data-testid="custom-tpl-description"/>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="custom-tpl-cancel">Cancel</Button>
              <Button
                variant="outline"
                onClick={() => setPreviewing(true)}
                disabled={!editing.subject || !editing.body}
                data-testid="custom-tpl-preview"
              >
                <Eye className="h-4 w-4 mr-1"/>Preview branded shell
              </Button>
              <Button className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90" onClick={save} data-testid="custom-tpl-save">Save template</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {editing && (
        <BrandedPreviewDialog
          open={previewing}
          onClose={() => setPreviewing(false)}
          subject={editing.subject || ""}
          body={editing.body || ""}
          H={H}
          brand="careers"
          label={editing.name || "Custom template"}
        />
      )}
    </div>
  );
}

/* ── Sequence editor ────────────────────────────────────────────────── */
function SequenceEditor({ H }) {
  const [seq, setSeq] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/outreach/sequences`, { headers: H })
      .then(r => setSeq(r.data.sequences?.[0] || null))
      .catch(e => toast.error(e?.response?.data?.detail || e.message));
  }, [H]);
  useEffect(() => { load(); }, [load]);

  if (!seq) return <div className="text-sm text-[#6F8FA8]">Loading…</div>;
  const setStep = (i, patch) => {
    setSeq(s => {
      const steps = [...s.steps];
      steps[i] = { ...steps[i], ...patch };
      return { ...s, steps };
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/outreach/sequences`, seq, { headers: H });
      toast.success("Sequence saved");
      load();
    } catch (e) { toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="outreach-sequence-editor">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-[#0D2F4D]">{seq.name}</p>
          <p className="text-xs text-[#6F8FA8]">3-step template · sent from {seq.from_email}</p>
        </div>
        <Button className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90" onClick={save} disabled={saving} data-testid="outreach-sequence-save">
          {saving ? "Saving…" : "Save sequence"}
        </Button>
      </div>
      {seq.steps.map((step, i) => (
        <Card key={i} className="p-5 space-y-3" data-testid={`outreach-step-${i}`}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-[#0D2F4D]">Step {i+1} — {step.label}</p>
            {i > 0 && (
              <span className="text-xs text-[#6F8FA8] inline-flex items-center gap-1">
                <Clock className="h-3 w-3"/>
                Wait
                <Input type="number" min="0" value={step.wait_days_after_prev} className="w-16 h-7 text-xs"
                  onChange={e => setStep(i, { wait_days_after_prev: parseInt(e.target.value || "0", 10) })}
                  data-testid={`outreach-step-${i}-wait`}/>
                days after previous
              </span>
            )}
          </div>
          <Input value={step.label} onChange={e => setStep(i, { label: e.target.value })} placeholder="Step label" data-testid={`outreach-step-${i}-label`}/>
          <Input value={step.subject} onChange={e => setStep(i, { subject: e.target.value })} placeholder="Subject line" data-testid={`outreach-step-${i}-subject`}/>
          <MarkdownEditor value={step.body} onChange={(v) => setStep(i, { body: v })} rows={10} testId={`outreach-step-${i}-body`}/>
        </Card>
      ))}
    </div>
  );
}

/* ── Enrollments ────────────────────────────────────────────────────── */
function Enrollments({ H }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/admin/outreach/enrollments`, { headers: H })
      .then(r => setRows(r.data.enrollments || []))
      .catch(e => toast.error(e?.response?.data?.detail || e.message));
  }, [H]);
  useEffect(() => { load(); }, [load]);

  const stop = async (id) => {
    if (!window.confirm("Stop this sequence? Remaining steps won't send.")) return;
    try {
      await axios.post(`${API}/admin/outreach/enrollments/${id}/stop`, {}, { headers: H });
      toast.success("Stopped"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const badge = (s) => {
    const map = { draft: "bg-slate-100 text-slate-800", waiting: "bg-amber-100 text-amber-800",
                  completed: "bg-emerald-100 text-emerald-800", stopped: "bg-red-100 text-red-800" };
    return <Badge className={map[s] || "bg-slate-100"}>{s}</Badge>;
  };

  return (
    <Card>
      <Table data-testid="outreach-enrollments-table">
        <TableHeader>
          <TableRow>
            <TableHead>Prospect</TableHead>
            <TableHead>Sequence</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Step</TableHead>
            <TableHead>Next send</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(e => (
            <TableRow key={e.id} data-testid={`outreach-enrollment-${e.id}`}>
              <TableCell className="text-sm">{e.prospect_email}</TableCell>
              <TableCell className="text-xs">{e.sequence_name}</TableCell>
              <TableCell>{badge(e.status)}</TableCell>
              <TableCell className="text-xs">{e.current_step}/3</TableCell>
              <TableCell className="text-xs">{e.next_send_at ? new Date(e.next_send_at).toLocaleString() : "—"}</TableCell>
              <TableCell className="text-right">
                {(e.status === "waiting" || e.status === "draft") && (
                  <Button size="sm" variant="ghost" onClick={() => stop(e.id)} data-testid={`outreach-stop-${e.id}`}>
                    <StopCircle className="h-4 w-4 text-red-500"/>
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (<TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-[#6F8FA8]">No enrollments yet. Enroll a prospect from the Prospects tab.</TableCell></TableRow>)}
        </TableBody>
      </Table>
    </Card>
  );
}

export default OutreachTab;
